import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Globe, Users, Radio, Calendar as CalendarIcon } from 'lucide-react';
import { creatorAudienceApi } from '../lib/api';
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup,
} from 'react-simple-maps';

const GEO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

const PERIOD_OPTIONS = [
  { value: 'day', label: 'Today' },
  { value: 'week', label: 'Last 7 Days' },
  { value: 'custom', label: 'Custom' },
];

// ISO alpha-2 → ISO numeric (world-atlas topojson uses numeric IDs)
const A2_TO_NUM = {
  US: '840', GB: '826', DE: '276', FR: '250', IT: '380', ES: '724', BR: '076',
  CA: '124', AU: '036', JP: '392', IN: '356', CN: '156', MX: '484', KR: '410',
  RU: '643', AR: '032', NL: '528', SE: '752', CH: '756', PL: '616', PT: '620',
  BE: '056', AT: '040', NO: '578', DK: '208', FI: '246', IE: '372', CZ: '203',
  RO: '642', GR: '300', TR: '792', ZA: '710', NG: '566', EG: '818', PH: '608',
  ID: '360', TH: '764', VN: '704', MY: '458', SG: '702', NZ: '554', CL: '152',
  CO: '170', PE: '604', SA: '682', AE: '784', IL: '376', UA: '804', HU: '348',
  SK: '703', HR: '191', BG: '100', RS: '688', LT: '440', LV: '428', EE: '233',
};

// Country name → ISO numeric fallback
const NAME_TO_NUM = {
  'United States': '840', 'United Kingdom': '826', Germany: '276', France: '250',
  Italy: '380', Spain: '724', Brazil: '076', Canada: '124', Australia: '036',
  Japan: '392', India: '356', China: '156', Mexico: '484', 'South Korea': '410',
  Russia: '643', Argentina: '032', Netherlands: '528', Sweden: '752',
  Switzerland: '756', Poland: '616', Portugal: '620', Belgium: '056',
  Austria: '040', Norway: '578', Denmark: '208', Finland: '246',
  Ireland: '372', 'Czech Republic': '203', Romania: '642', Greece: '300',
  Turkey: '792', 'South Africa': '710', Nigeria: '566', Egypt: '818',
  Philippines: '608', Indonesia: '360', Thailand: '764', Vietnam: '704',
  Malaysia: '458', Singapore: '702', 'New Zealand': '554', Chile: '152',
  Colombia: '170', Peru: '604', 'Saudi Arabia': '682', 'United Arab Emirates': '784',
  Israel: '376', Ukraine: '804', Hungary: '348', Slovakia: '703',
  Croatia: '191', Bulgaria: '100', Serbia: '688', Lithuania: '440',
  Latvia: '428', Estonia: '233',
};

const CreatorAudienceTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('week');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [tooltip, setTooltip] = useState({ show: false, content: '', x: 0, y: 0 });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await creatorAudienceApi.getAudience(period, startDate, endDate);
      setData(result);
    } catch (e) {
      console.error('Failed to load audience data:', e);
    } finally {
      setLoading(false);
    }
  }, [period, startDate, endDate]);

  useEffect(() => {
    if (period === 'custom' && (!startDate || !endDate)) return;
    fetchData();
  }, [fetchData, period, startDate, endDate]);

  useEffect(() => {
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const countries = data?.countries || [];
  const totalViews = data?.total_views || 0;
  const liveViewers = data?.live_viewers || 0;

  // Build a lookup: ISO numeric code → viewer count for map coloring
  const countryViewMap = useMemo(() => {
    const map = {};
    for (const c of countries) {
      const num = A2_TO_NUM[c.code] || NAME_TO_NUM[c.country];
      if (num) map[num] = (map[num] || 0) + c.viewers;
    }
    return map;
  }, [countries]);

  const maxViewers = useMemo(
    () => Math.max(1, ...Object.values(countryViewMap)),
    [countryViewMap],
  );

  const getCountryColor = (geoId) => {
    const count = countryViewMap[geoId];
    if (!count) return '#1a1040';
    const intensity = Math.min(1, count / maxViewers);
    // Gradient from dim purple to bright purple
    const r = Math.round(30 + intensity * 109);  // 30→139
    const g = Math.round(16 + intensity * 76);    // 16→92
    const b = Math.round(64 + intensity * 182);   // 64→246
    return `rgb(${r}, ${g}, ${b})`;
  };

  const handleMouseEnter = (geo, evt) => {
    const geoId = geo.id;
    const name = geo.properties?.name || geo.properties?.NAME || geoId;
    const count = countryViewMap[geoId] || 0;
    if (count > 0) {
      setTooltip({
        show: true,
        content: `${name}: ${count} view${count !== 1 ? 's' : ''}`,
        x: evt.clientX,
        y: evt.clientY,
      });
    }
  };

  const handleMouseLeave = () => {
    setTooltip({ show: false, content: '', x: 0, y: 0 });
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="creator-audience-tab">
      {/* Header row: stats + filters */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex flex-wrap gap-4">
          <div className="admin-card px-5 py-4 flex items-center gap-3" data-testid="audience-total-views">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#8B5CF6]/15">
              <Users className="w-5 h-5 text-[#8B5CF6]" />
            </div>
            <div>
              <p className="text-2xl font-bold tracking-tight">{totalViews}</p>
              <p className="text-xs text-gray-400">Views in Period</p>
            </div>
          </div>
          <div className="admin-card px-5 py-4 flex items-center gap-3" data-testid="audience-countries-count">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#00FF9D]/10">
              <Globe className="w-5 h-5 text-[#00FF9D]" />
            </div>
            <div>
              <p className="text-2xl font-bold tracking-tight">{countries.length}</p>
              <p className="text-xs text-gray-400">Countries</p>
            </div>
          </div>
          <div className="admin-card px-5 py-4 flex items-center gap-3" data-testid="audience-live-count">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-green-500/10">
              <Radio className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold tracking-tight">{liveViewers}</p>
              <p className="text-xs text-gray-400">Live Now</p>
            </div>
          </div>
        </div>

        {/* Time Filters */}
        <div className="flex flex-wrap items-center gap-2" data-testid="audience-time-filters">
          {PERIOD_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => setPeriod(opt.value)}
              className={`text-xs px-3 py-1.5 rounded-lg transition-colors ${
                period === opt.value
                  ? 'bg-[#8B5CF6] text-white'
                  : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
              data-testid={`audience-filter-${opt.value}`}
            >
              {opt.label}
            </button>
          ))}
          {period === 'custom' && (
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                className="bg-[#0F0B1E] border border-[#8B5CF6]/20 rounded-lg px-2 py-1 text-xs text-gray-300"
                data-testid="audience-start-date"
              />
              <span className="text-gray-500 text-xs">to</span>
              <input
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                className="bg-[#0F0B1E] border border-[#8B5CF6]/20 rounded-lg px-2 py-1 text-xs text-gray-300"
                data-testid="audience-end-date"
              />
            </div>
          )}
        </div>
      </div>

      {/* Map + Country List */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* 2D Choropleth World Map */}
        <div className="lg:col-span-2 admin-card p-0 overflow-hidden relative" style={{ minHeight: '420px' }} data-testid="creator-audience-map">
          <ComposableMap
            projectionConfig={{ rotate: [-10, 0, 0], scale: 155 }}
            style={{ width: '100%', height: '100%' }}
            width={800}
            height={420}
          >
            <ZoomableGroup center={[0, 20]} zoom={1}>
              <Geographies geography={GEO_URL}>
                {({ geographies }) =>
                  geographies.map((geo) => {
                    const geoId = geo.id;
                    const hasData = !!countryViewMap[geoId];
                    return (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        fill={getCountryColor(geoId)}
                        stroke={hasData ? '#8B5CF6' : '#2a1f50'}
                        strokeWidth={hasData ? 1 : 0.3}
                        style={{
                          default: { outline: 'none' },
                          hover: {
                            fill: hasData ? '#A78BFA' : '#251a4a',
                            outline: 'none',
                            cursor: hasData ? 'pointer' : 'default',
                          },
                          pressed: { outline: 'none' },
                        }}
                        onMouseEnter={(evt) => handleMouseEnter(geo, evt)}
                        onMouseLeave={handleMouseLeave}
                      />
                    );
                  })
                }
              </Geographies>
            </ZoomableGroup>
          </ComposableMap>

          {/* Tooltip */}
          {tooltip.show && (
            <div
              className="fixed z-50 px-3 py-1.5 rounded-lg text-xs text-white pointer-events-none"
              style={{
                left: tooltip.x + 12,
                top: tooltip.y - 28,
                background: '#1a1040',
                border: '1px solid rgba(139,92,246,0.4)',
              }}
            >
              {tooltip.content}
            </div>
          )}

          {/* Empty state overlay */}
          {totalViews === 0 && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/40 z-10">
              <div className="text-center">
                <Globe className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400 text-sm">No audience data for this period</p>
                <p className="text-gray-600 text-xs mt-1">Viewers will appear when users watch your content</p>
              </div>
            </div>
          )}

          {/* Legend */}
          {totalViews > 0 && (
            <div className="absolute bottom-3 left-3 flex items-center gap-2 bg-black/60 backdrop-blur-sm px-3 py-1.5 rounded-lg">
              <span className="text-[10px] text-gray-400">Less</span>
              <div className="flex gap-0.5">
                {[0.1, 0.3, 0.5, 0.7, 1.0].map((i) => (
                  <div
                    key={i}
                    className="w-4 h-2.5 rounded-sm"
                    style={{
                      backgroundColor: `rgb(${Math.round(30 + i * 109)}, ${Math.round(16 + i * 76)}, ${Math.round(64 + i * 182)})`,
                    }}
                  />
                ))}
              </div>
              <span className="text-[10px] text-gray-400">More</span>
            </div>
          )}
        </div>

        {/* Country List */}
        <div className="space-y-4">
          <div className="admin-card p-5" data-testid="creator-audience-countries">
            <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
              <Globe className="w-4 h-4 text-[#8B5CF6]" /> Audience by Country
            </h3>
            {countries.length === 0 ? (
              <p className="text-xs text-gray-500 py-6 text-center">No audience data yet for this period</p>
            ) : (
              <div className="space-y-2.5 max-h-[400px] overflow-y-auto pr-1">
                {countries.map((c, i) => (
                  <div key={i} className="flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-gray-300 truncate">{c.country}</span>
                        <span className="text-xs text-[#8B5CF6] font-semibold tabular-nums ml-2">{c.percentage}%</span>
                      </div>
                      <div className="w-full h-1.5 bg-white/5 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[#8B5CF6] rounded-full transition-all"
                          style={{ width: `${c.percentage}%` }}
                        />
                      </div>
                    </div>
                    <span className="text-xs text-gray-500 tabular-nums w-10 text-right flex-shrink-0">{c.viewers}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Date range info */}
          {data?.date_range && (
            <div className="admin-card p-4 text-center" data-testid="audience-date-info">
              <p className="text-xs text-gray-500 flex items-center justify-center gap-1.5">
                <CalendarIcon className="w-3.5 h-3.5" />
                {data.date_range.from === data.date_range.to
                  ? data.date_range.from
                  : `${data.date_range.from} — ${data.date_range.to}`}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreatorAudienceTab;
